import { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { TranslationMemorySegmentRegex } from "./types";
import { Option } from "../components/tailwind/dropdown/Dropdown";
import { getTranslationMemorySegmentRegexList } from "../api/translationMemoryApi";

type SegmentRegexHookReturn = {
  regexes: TranslationMemorySegmentRegex[];
  isFetchingRegex: boolean;
  regexId: number | null;
  setRegexId: (regexId: number | null) => void;
  regex: TranslationMemorySegmentRegex | null;
  regexOptions: Option<number>[];
  addRegex: (regex: string, id: number, name?: string) => void;
};

export const useSegmentRegex = (
  initialRegexId: number | null,
  controlInputId: string | null
): SegmentRegexHookReturn => {
  const mounted = useRef<boolean>(true);
  const [segmentRegexes, setSegmentRegexes] = useState<
    TranslationMemorySegmentRegex[]
  >([]);
  const [isFetchingRegex, setIsFetchingRegex] = useState<boolean>(false);

  const [regexId, setRegexStateId] = useState<number | null>(initialRegexId);

  const regex = useMemo(() => {
    return segmentRegexes.find((option) => option.id === regexId);
  }, [regexId, segmentRegexes]);

  const regexOptions = useMemo<Option<number>[]>(
    () =>
      segmentRegexes.map((regex) => ({
        text: `${regex?.default ? "⭐ " : ""}${regex.name || regex.regex}`,
        key: regex.id.toString(),
        value: regex.id,
        caption: regex.regex,
      })),
    [segmentRegexes]
  );

  const setRegexId = useCallback(
    (regexId: number | null) => {
      setRegexStateId(regexId);
      if (controlInputId) {
        const input = document.getElementById(
          controlInputId
        ) as HTMLInputElement;
        if (input) {
          input.value = regexId?.toString() ?? "";
        }
      }
    },
    [controlInputId, setRegexStateId]
  );

  const addRegex = useCallback(
    (regex: string, id: number, name?: string): void => {
      setSegmentRegexes([...segmentRegexes, { id, name, regex }]);
      setRegexStateId(id);
    },
    [segmentRegexes, setRegexStateId]
  );

  useEffect(() => {
    mounted.current = true;
    return (): void => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    mounted.current = true;
    if (mounted.current) {
      setIsFetchingRegex(true);
      getTranslationMemorySegmentRegexList()
        .then((data) => {
          if (mounted.current) {
            setSegmentRegexes(data);
          }
        })
        .finally(() => {
          if (mounted.current) {
            setIsFetchingRegex(false);
          }
        });
    }
    return () => {
      mounted.current = false;
    };
  }, []);

  return {
    regexes: segmentRegexes,
    isFetchingRegex,
    regexId,
    setRegexId,
    addRegex,
    regex,
    regexOptions,
  };
};
